import { useEffect, useMemo, useState } from "react";
import { getCauseTranslationOccurrences } from "utils/moods/moodAnalytics";
import type { Mood } from "../../../types/models";
import { getTeamCauses } from "@/api/causeApi";
import { useTeam } from "@/contexts/TeamContext";

export interface UseCauseAnalysisReturn {
  filteredCauses: Record<string, number>;
  burnoutCauses: Record<string, number>;
  balancedCauses: Record<string, number>;
  teamCausesYes: Record<string, number>;
}

export function useCauseAnalysis(
  moods: Mood[],
  causes: unknown[],
  allCauseTypes: unknown[],
  causeTypesTranslations: unknown[]
): UseCauseAnalysisReturn {
  const safeCauses = Array.isArray(causes) ? causes : [];
  const safeAllCauseTypes = Array.isArray(allCauseTypes) ? allCauseTypes : [];
  const safeCauseTypesTranslations = Array.isArray(causeTypesTranslations)
    ? causeTypesTranslations
    : [];


  const { teamId } = useTeam();
  const [teamCausesYes, setTeamCausesYes] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    getTeamCauses(teamId)
      .then((nextCauses) => {
        if (!cancelled) setTeamCausesYes(nextCauses ?? {});
      })
      .catch((error) => {
        console.error("Failed to load team causes:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const filteredCauses = useMemo(() => {
    return getCauseTranslationOccurrences(
      moods,
      safeCauses,
      safeAllCauseTypes,
      safeCauseTypesTranslations
    );
  }, [moods, safeCauses, safeAllCauseTypes, safeCauseTypesTranslations]);

  const burnoutCauses = useMemo(() => {
    const anxietyApathyMoods = moods.filter(
      (mood: Mood) => mood.mood === "Anxiety" || mood.mood === "Apathy"
    );
    return getCauseTranslationOccurrences(
      anxietyApathyMoods,
      safeCauses,
      safeAllCauseTypes,
      safeCauseTypesTranslations
    );
  }, [moods, safeCauses, safeAllCauseTypes, safeCauseTypesTranslations]);

  const balancedCauses = useMemo(() => {
    const flowConfidentMoods = moods.filter(
      (mood: Mood) => mood.mood === "Flow" || mood.mood === "Confident"
    );
    return getCauseTranslationOccurrences(
      flowConfidentMoods,
      safeCauses,
      safeAllCauseTypes,
      safeCauseTypesTranslations
    );
  }, [moods, safeCauses, safeAllCauseTypes, safeCauseTypesTranslations]);

  return {
    filteredCauses,
    burnoutCauses,
    balancedCauses,
    teamCausesYes
  };
}
